import { useLingui } from "@lingui/react/macro";
import { Button } from "@offstreetparking/design-system/forms";
import { AddIcon } from "@offstreetparking/design-system/icons/AddIcon";
import { Text } from "@offstreetparking/design-system/typography";
import { PermitStatus } from "@offstreetparking/domain/value/permits";
import { useState } from "react";
import { offstreetClient } from "../../../../api/client";
import { ServerErrorAlert } from "../../../forms/Alert";
import { VoidPermitModal } from "../../../forms/Permit/VoidPermitModal";
import { PermitInstructionsOverlay } from "./PermitInstructionsOverlay";
type PermitActionsSectionProps = {
	permitStatus: PermitStatus;
	publicAccessToken?: string | null;
	parkerId?: number;
	isEditPermitDisabled: boolean;
	isCancelPermitDisabled: boolean;
	addAnotherOption?: () => void;
	publicIdentifier: string;
	selfServiceInstructions?: string | null;
};
export const PermitActionsSection = ({
	permitStatus,
	publicAccessToken,
	parkerId,
	isEditPermitDisabled,
	isCancelPermitDisabled,
	addAnotherOption,
	publicIdentifier,
	selfServiceInstructions,
}: PermitActionsSectionProps) => {
	const { t } = useLingui();
	const [isDeletePermitModalOpen, setIsDeletePermitModalOpen] = useState<boolean>(false);
	const [isInstructionsOpen, setIsInstructionsOpen] = useState<boolean>(false);
	const utils = offstreetClient.useUtils();
	const {
		mutate: cancelPermit,
		isPending,
		error: cancelPermitError,
	} = offstreetClient.permit.cancelPermitPublic.useMutation({
		onMutate: async () => {
			await utils.permit.getPermitByPublicIdentifier.cancel({
				publicIdentifier,
			});
			const previousPermit = utils.permit.getPermitByPublicIdentifier.getData({
				publicIdentifier,
			});
			if (previousPermit) {
				utils.permit.getPermitByPublicIdentifier.setData(
					{
						publicIdentifier,
					},
					{
						...previousPermit,
						permitStatus: PermitStatus.VOID,
					},
				);
			}
			return {
				previousPermit,
			};
		},
		onError: (_err, _variables, context) => {
			if (context?.previousPermit) {
				utils.permit.getPermitByPublicIdentifier.setData(
					{
						publicIdentifier,
					},
					context.previousPermit,
				);
			}
		},
		onSuccess: () => {
			window.scrollTo({
				top: 0,
				behavior: "smooth",
			});
		},
		onSettled: () => {
			utils.permit.getPermitByPublicIdentifier.invalidate({
				publicIdentifier,
			});
		},
	});

	const isCancelAvailable = permitStatus === PermitStatus.UPCOMING && !isCancelPermitDisabled;
	const isEditAvailable =
		(permitStatus === PermitStatus.ACTIVE || permitStatus === PermitStatus.UPCOMING) &&
		(!!publicAccessToken || !!parkerId) &&
		!isEditPermitDisabled;
	const showInstructionsLink = !!selfServiceInstructions && (!isEditAvailable || !isCancelAvailable);

	const promptText =
		!isEditAvailable && !isCancelAvailable
			? t`Need to edit or cancel your permit?`
			: !isEditAvailable
				? t`Need to edit your permit?`
				: t`Need to cancel your permit?`;
	const linkText =
		!isEditAvailable && !isCancelAvailable
			? t`View instructions`
			: !isEditAvailable
				? t`View edit instructions`
				: t`View cancellation instructions`;

	return (
		<div className="print:hidden">
			<ServerErrorAlert serverError={cancelPermitError} />

			{!!publicAccessToken && (
				<div className="mt-4 flex flex-col-reverse items-center gap-4 sm:flex-row">
					{isCancelAvailable && (
						<div className="w-full sm:w-auto">
							<VoidPermitModal
								isModalOpen={isDeletePermitModalOpen}
								setIsModalOpen={setIsDeletePermitModalOpen}
								onDelete={() => {
									if (!publicAccessToken) {
										return;
									}
									cancelPermit({
										publicIdentifier,
										token: publicAccessToken,
									});
								}}
							/>
							<Button
								intent="secondary"
								className="w-full sm:w-auto"
								isDestructive={true}
								isLoading={isPending}
								onClick={() => setIsDeletePermitModalOpen(true)}
							>
								{t`Cancel Permit`}
							</Button>
						</div>
					)}

					{addAnotherOption && (
						<Button
							intent="secondary"
							leftIcon={AddIcon}
							className="w-full sm:ml-auto sm:w-auto"
							onClick={addAnotherOption}
						>
							{t`Create Another Permit`}
						</Button>
					)}
				</div>
			)}

			{showInstructionsLink && (
				<div className="mt-6 flex flex-col items-center gap-1 text-center">
					<Text>{promptText}</Text>
					<Button intent="action" onClick={() => setIsInstructionsOpen(true)}>
						{linkText}
					</Button>
					<PermitInstructionsOverlay
						isOpen={isInstructionsOpen}
						onClose={() => setIsInstructionsOpen(false)}
						selfServiceInstructions={selfServiceInstructions ?? ""}
						isEditPermitAvailable={isEditAvailable}
						isCancelPermitAvailable={isCancelAvailable}
					/>
				</div>
			)}
		</div>
	);
};
